

import Vue from "vue";
import { searchTransactions } from "@/api/declinedTransactions/api";
import { SearchDeclinedTransactionDto } from "@/api/declinedTransactions/types";
import { formatDate, parseDate } from "@/helpers/date";
import store from "@/store";
import { IDeclinedTransactionFilters } from "@/store/declinedTransactions/types";
import { Parser } from "@json2csv/plainjs";

export default Vue.extend({
  data() {
    return {
      isLoading: false,
    };
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    filters: {
      type: Object as () => IDeclinedTransactionFilters,
      required: true,
    },
  },
  methods: {
    /**
     * Triggered when the users clicks the export button.
     * Exports the filtered data as a new CSV file.
     */
    async onExport() {
      if (this.isLoading) return;

      this.isLoading = true;

      // Fetch the latest up to date data
      const sourceRecords = await getUnlimitedSearchData(this.filters);

      // Map these into flat CSV data
      const csvRecords = getCSVRowsForTransactions(sourceRecords);

      // Run the parser on our data
      const parser = new Parser();
      const csvData = parser.parse(csvRecords);

      const fileName = `DeclinedTransactionReport${formatDate(
        new Date(),
        "yyyyMMddhhmmss"
      )}.csv`;
      const fileType = "text/csv";

      // Download the data to the users browser
      downloadFileDataToBrowser(csvData, fileType, fileName);

      this.isLoading = false;
    },
  },
});

/**
 * Returns the full set of search data, using the currently selected filters
 */
async function getUnlimitedSearchData(filters: IDeclinedTransactionFilters) {
  // Having an undefined value for take ensures that any and all records will be returned
  return (
    await searchTransactions(
      undefined,
      0,
      filters.cardNumber,
      filters.locationCode,
      filters.parentTerminalId,
      filters.childTerminalId,
      filters.declineCode,
      filters.voucherTransactionNo,
      filters.totalTransactionValue,
      filters.startDateTime,
      filters.endDateTime
    )
  ).results;
}

/**
 * Downloads a file within the browser
 */
function downloadFileDataToBrowser(
  fileData: string,
  fileContentType: string,
  fileName: string
) {
  const element = document.createElement("a");

  element.setAttribute(
    "href",
    `data:${fileContentType};charset=utf-8,${encodeURIComponent(fileData)}`
  );

  element.setAttribute("download", fileName);

  element.style.display = "none";
  document.body.appendChild(element);

  element.click();
  document.body.removeChild(element);
}

/**
 * Maps the transactions retrived from the API into a set of flat rows
 */
function getCSVRowsForTransactions(
  transactions: SearchDeclinedTransactionDto[]
) {
  return transactions.map((transaction) =>
    getCSVRecordForTransaction(transaction)
  );
}

/**
 * Creates the CSV record for a given transaction. Produces a
 * flat list of fields. For the nested transaction lines, these are outputted as additional
 * columns which are numbered.
 */
function getCSVRecordForTransaction(transaction: SearchDeclinedTransactionDto) {
  const newRecord: { [label: string]: any } = {
    "Location Code": transaction.locationCode,
    "Location Name": transaction.locationName,
    "Transaction Date": formatDate(transaction.transactionDateTime, "dd/MM/yyyy HH:mm:ss"),
    "Voucher-Transaction No": transaction.voucherTransactionNo,
    "Card Number": transaction.cardNumber,
    "Odometer ": transaction.odometer,
    "Capture Type": transaction.captureType,
    "Total Transaction Value ($)": transaction.totalTransactionValue,
    "Master Terminal ID": transaction.parentTerminalId,
    "Slave Terminal ID": transaction.childTerminalId,
    "Decline Code": transaction.declineCode,
    "Decline Reason": getDeclineDescription(transaction),
  };

  if (transaction.transactionLines) {
    transaction.transactionLines.forEach((value, index) => {
      newRecord[`Product Code (${Number(index) + 1})`] = value.productCode;
      newRecord[`Pump Price (${Number(index) + 1})`] = value.unitPrice;
      newRecord[`Quantity (${Number(index) + 1})`] = value.quantity;
    });
  }

  return newRecord;
}

/**
 * Returns the decline description for a declined transaction, and if non is found
 * returns an empty string.
 */
function getDeclineDescription(transaction: SearchDeclinedTransactionDto) {
  const declineCodes = store.state.declinedTransactions.declineCodes;
  const declineCodeEntity = declineCodes.find(
    (code) => code.declineCode === transaction.declineCode
  );

  return declineCodeEntity ? declineCodeEntity.declineDescripton : "";
}
